import Action from '@/common/action'
import { Id } from '@/common/base-types'
import { DebugFlag, SettingKey } from '@/common/constants'
import {
  CAMERA_ANIMATION_DURATION_IN,
  CAMERA_ANIMATION_DURATION_OUT,
  ColliderType,
  SpawnLocation
} from '@/features/experience/misc/constants'
import supportedTokens from '@/features/experience/configs/supported-tokens'
import RendererDebugInfo from '@/features/experience/misc/renderer-debug-info'
import PostEffectManager from '@/features/experience/posteffects/posteffects-manager'
import { Interface } from '@/shared/di/interface'
import type { IAssets } from '@/shared/di/interface/assets'
import type { IDebug } from '@/shared/di/interface/debug'
import type { ISize } from '@/shared/di/interface/size'
import type { ISound } from '@/shared/di/interface/sound'
import type { ISwipeArea, ITouch } from '@/shared/di/interface/touch'
import { IWalletFaces, WalletLoadingErrorType } from '@/shared/di/interface/walletfaces'
import globalRenderer from '@/shared/misc/global-renderer'
import { Asset, IMoveActionData, ITransferActionData, RefSource, UpdateInfo, ITokenDetail } from '@/state/vault/domain'
import { Expo, gsap } from 'gsap'
import { Camera, Clock, MeshBasicMaterial, PerspectiveCamera, Raycaster, Vector2, Vector3, WebGLRenderer } from 'three'
import { container } from 'tsyringe'

import RoomBase, { RoomConnectionSlot, StaggerType } from './rooms/room-base'

const raycaster = new Raycaster()

export interface IVaultExperience {
  isParent: boolean
  userId: string

  userName: string
  walletFaceId: string
  hasRenderedOnce: boolean
  isDestroyed: boolean
  activeRoom: RoomBase
  defaultRoom: RoomBase

  get openForIncomingEvents(): boolean

  init(walletFaceId: string): void

  swapWalletFace(walletFaceId: string): Promise<void>

  enable(): void

  disable(): void

  enableInteractions(): void

  disableInteractions(): void

  setActiveRoom(room: string): string

  setActiveContactWalletId(id: string): void

  hideAllTubes(): Promise<void>

  assetsUpdate(update: Asset[], updateInfo: UpdateInfo): void

  assetsAdded(assets: Asset[], updateInfo: UpdateInfo): void

  assetsRemoved(assets: string[]): void

  returnInvalidTransfer(asset: Asset): void

  remotePoke(type: string, fromWalletId: string, data: any): void

  update(time: number, delta: number): void

  destroy(): void

  getRoomManager(room: string): RoomBase

  removeAllTubes(): void

  appPaused(): void

  appResumed(): void

  //event
  onWalletFaceLoadStart: Action<void>
  onWalletFaceLoadComplete: Action<void>
  onTubeClosed: Action<void>
  onRoomChangeRequest: Action<string>
  onUpdate: Action<void>
  onTokenEnteredTube: Action<ITransferActionData>
  onTokenDispose: Action<string>
  onCoinChangeRoom: Action<IMoveActionData>
  onCoinsCombine: Action<Asset[]>
  onCoinDivide: Action<Asset[]>
  onCoinRemove: Action<Id>
  onOpenForEventChanged: Action<boolean>
  onRenderInfo: Action<any>
  onNewAssetsArrived: Action<Asset[]>
  onNewAssetsAnimating: Action<void>
  onCoinZoomedIn: Action<ITokenDetail | undefined>
  onUpdateUxData: Action<Asset[]>
  onRevealItemAdded: Action<Asset>
  onRevealStarted: Action<void>
}

export interface RoomTransitionEvent {
  from: string
  to: string
}
export default class VaultExperienceBase implements IVaultExperience {
  isParent = false
  isLoadingWalletFace = false
  userId = ''
  walletId = ''
  userName = ''
  walletFaceId = ''
  hasRenderedOnce = false
  isDestroyed = false
  rooms: Map<string, RoomBase> = new Map()
  defaultRoom: RoomBase = {} as RoomBase
  activeRoom: RoomBase = {} as RoomBase
  activeContactWalletId = ''
  postEffectManager: PostEffectManager = {} as PostEffectManager

  protected newCoinsInWallets: Map<string, number> = new Map()

  #running = false
  #appPaused = false
  #time = 0
  #clock = new Clock()
  #tokenRoomMap: Map<string, string> = new Map()
  #lastRoomLoaded = ''
  #openForIncomingEvents = false
  debugInfo: any = { fps: 0, renderCalls: 0 }
  #renderer: WebGLRenderer = {} as WebGLRenderer
  #rendererDebugInfo: RendererDebugInfo | undefined
  #isInTransition = false

  #cameraParallaxOffsetTarget = new Vector3()
  #bloomListIsDirty = false
  #activeWalletChangedTimeout: number | undefined
  #shouldUseRevealAnimation = false
  //used by experience
  onRoomTransitionStart = new Action<RoomTransitionEvent>()
  onRoomTransitionComplete = new Action<string>()
  onUpdate = new Action<void>()

  //expose emitters for View
  onWalletFaceLoadStart = new Action<void>()
  onWalletFaceLoadComplete = new Action<void>()
  onTubeClosed = new Action<void>()
  onRoomChangeRequest = new Action<string>()
  onTokenEnteredTube = new Action<ITransferActionData>()
  onTokenDispose = new Action<string>()
  onCoinChangeRoom = new Action<IMoveActionData>()
  onCoinsCombine = new Action<Asset[]>()
  onCoinDivide = new Action<Asset[]>()
  onNewAssetsAnimating = new Action<void>()
  onNewAssetsArrived = new Action<Asset[]>()
  onCoinRemove = new Action<Id>()
  onOpenForEventChanged = new Action<boolean>()
  onRenderInfo = new Action<any>()
  onCoinZoomedIn = new Action<ITokenDetail | undefined>()
  onUpdateUxData = new Action<Asset[]>()
  onRevealItemAdded = new Action<Asset>()
  onRevealStarted = new Action<void>()

  constructor() {
    const assets = container.resolve<IAssets>(Interface.IAssets)
    const hitBoxMaterial = new MeshBasicMaterial({
      visible: false
    })
    assets.addMaterial('hitBox', hitBoxMaterial, 'global')
  }

  enable() {
    if (this.#running || this.isLoadingWalletFace) {
      return
    }

    this.onCoinChangeRoom.addEarly(this.onCoinChangeRoomHandler)
    this.onTokenEnteredTube.addEarly(this.onTokenEnteredTubeHandler)

    this.enableInteractions()

    this.activeRoom.activate()
    //this.#time = 0
    this.#running = true
    this.gameLoop()
  }

  disable() {
    if (!this.#running) {
      return
    }

    this.onCoinChangeRoom.remove(this.onCoinChangeRoomHandler)
    this.onTokenEnteredTube.remove(this.onTokenEnteredTubeHandler)
    this.#running = false
    //this.isDestroyed = true
    this.disableInteractions()
    this.rooms.forEach(room => {
      room.inactivate()
    })
  }

  appPaused(): void {
    this.#appPaused = true
  }

  appResumed(): void {
    this.#appPaused = false
    this.#clock.getDelta()
  }

  enableInteractions() {
    this.disableInteractions()
    const touch = container.resolve<ITouch>(Interface.ITouch)
    touch.onTap.add(this.onTap)
  }

  disableInteractions() {
    const touch = container.resolve<ITouch>(Interface.ITouch)
    touch.onTap.remove(this.onTap)
  }

  activateRevealedControllers() {
    this.activeRoom.activateRevealedControllers()
  }

  setActiveRoom(room: string): string {
    const checkRoom = this.getRoomManager(room)

    if (checkRoom) {
      this.animateToRoom(room)
      return checkRoom.config.id
    } else {
      this.animateToRoom(this.defaultRoom.config.id)
      return this.defaultRoom.config.id
    }
  }

  updateRoomSwipeAreas() {
    const touch = container.resolve<ITouch>(Interface.ITouch)

    if (this.activeRoom.config === undefined) {
      return
    }

    const connection = this.activeRoom.config.connectedRooms.find(c => c.slot === RoomConnectionSlot.Bottom)
    if (connection) {
      const area: ISwipeArea = {
        id: 'nav-down',
        originPercentageRect: { x: 0, y: 0.8, width: 1, height: 0.2 },
        angle: Math.PI * -0.5,
        angleMargin: Math.PI * 0.2,
        distance: 0.1,
        priority: 1,
        callback: () => {
          if (this.activeRoom.physics.interactions.isDragging) {
            return
          }
          this.onRoomChangeRequest.invoke(connection.id)
        }
      }

      touch.registerSwipeArea(area)
    } else {
      touch.unregisterSwipeArea('nav-down')
    }

    const connection2 = this.activeRoom.config.connectedRooms.find(c => c.slot === RoomConnectionSlot.Top)
    if (connection2) {
      const area: ISwipeArea = {
        id: 'nav-up',
        originPercentageRect: { x: 0, y: 0, width: 1, height: 0.2 },
        angle: Math.PI * 0.5,
        angleMargin: Math.PI * 0.2,
        distance: 0.1,
        priority: 1,
        callback: () => {
          if (this.activeRoom.physics.interactions.isDragging) {
            return
          }
          this.onRoomChangeRequest.invoke(connection2.id)
        }
      }

      touch.registerSwipeArea(area)
    } else {
      touch.unregisterSwipeArea('nav-up')
    }
  }

  setActiveContactWalletId(id: string) {
    this.activeContactWalletId = id

    this.rooms.forEach((room, key) => {
      room.setActiveContactId(id)
    })

    if (id !== '') {
      this.triggerDelayedFlush(2, this.#shouldUseRevealAnimation)
    }
  }

  get openForIncomingEvents() {
    return this.#openForIncomingEvents
  }

  set openForIncomingEvents(value: boolean) {
    this.#openForIncomingEvents = value
    this.onOpenForEventChanged.invoke(value)
  }

  prepareRoom() {
    this.rooms.forEach((room, key) => {
      room.setUserId(this.userId)
      room.setUserName(this.userName)
      room.walletId = this.walletId

      //just pass along. Need this because we don't use a global pub sub event system
      room.onTubeClosed.add((data: any) => {
        this.onTubeClosed.invoke(data)
      })
      room.onCoinZoomedIn.add((data: ITokenDetail | undefined) => {
        this.onCoinZoomedIn.invoke(data)
      })
      room.onCoinRemove.add(data => {
        this.onCoinRemove.invoke(data)
      })
      room.onCoinsCombine.add(data => {
        this.onCoinsCombine.invoke(data)
      })
      room.onCoinDivide.add(data => {
        this.onCoinDivide.invoke(data)
      })
      room.onCoinChangeRoom.add(data => {
        this.onCoinChangeRoom.invoke(data)
      })
      room.onTokenEnteredTube.add(data => {
        this.onTokenEnteredTube.invoke(data)
      })
      room.onNewAssetsArrived.add(data => {
        this.updateTubeCoins()
        this.onNewAssetsArrived.invoke(data)
      })
      room.onUpdateBloomList.add(() => {
        this.#bloomListIsDirty = true
      })
      room.onRevealItemAdded.add((a: Asset) => {
        this.onRevealItemAdded.invoke(a)
      })
      room.onRevealStarted.add(() => {
        this.onRevealStarted.invoke()
      })

      room.onTokenDispose.add(id => {
        this.removeAsset(id)
        this.onTokenDispose.invoke(id)
      })
    })
  }

  async init(walletFaceId: string) {
    this.walletFaceId = walletFaceId
    //this.#lastRoomLoaded = roomId
    const touch = container.resolve<ITouch>(Interface.ITouch)
    touch.unregisterAllSwipeAreas()

    this.#renderer = globalRenderer.getRenderer()

    this.#rendererDebugInfo = new RendererDebugInfo(this.#renderer)
    this.postEffectManager = new PostEffectManager(this.#renderer)
    this.#bloomListIsDirty = true
  }

  swapWalletFace(walletFaceId: string): Promise<void> {
    return Promise.resolve()
  }

  loadWalletFace(walletFaceId: string, newRooms: RoomBase[], forceStartRoom?: string): Promise<void> {
    this.isDestroyed = false
    this.isLoadingWalletFace = true

    return new Promise((resolve, reject) => {
      this.#tokenRoomMap.clear()
      this.onWalletFaceLoadStart.invoke()

      this.#bloomListIsDirty = true
      this.disable()
      this.unloadRooms()

      this.walletFaceId = walletFaceId

      newRooms.forEach(room => {
        room.walletFaceId = walletFaceId
        this.rooms.set(room.config.id, room)
      })

      this.prepareRoom()

      if (forceStartRoom) {
        const foundRoom = newRooms.find(r => r.config.id === forceStartRoom)
        if (foundRoom) {
          this.activeRoom = foundRoom
        } else {
          this.activeRoom = newRooms[0]
        }
      } else {
        this.activeRoom = newRooms[0]
      }

      this.defaultRoom = newRooms[0]

      newRooms.forEach(room => {
        this.rooms!.set(room.config.id, room)
        room.resize()
      })

      const loadRoom = async () => {
        const assets = container.resolve<IAssets>(Interface.IAssets)

        const loadingManifest = this.activeRoom.getAdjustedManifest()
        /*loadingManifest.forEach(item => {
          item.url = walletFaces.getUrl(item.url, walletFaceId)
        })*/

        try {
          await assets.loadRoomManifest(this.activeRoom.config.id, loadingManifest)

          /*
          test throw error
          if (walletFaceId !== DEFAULT_WALLET_FACE_ID) {
            throw new Error(WalletLoadingErrorType.LoadingAssets)
          }*/
        } catch (err) {
          this.isDestroyed = true
          reject(WalletLoadingErrorType.LoadingAssets)
        }

        if (this.isDestroyed) {
          return
        }

        //try {
        await this.activeRoom.init()
        /*} catch (err) {
          this.isDestroyed = true
          reject(WalletLoadingErrorType.GenericError)
        }*/

        if (this.isDestroyed) {
          return
        }

        this.postEffectManager.setSceneAndCamera(this.activeRoom.scene, this.activeRoom.camera as Camera)

        //this.animateToRoom(this.activeRoom.config.id)

        this.isLoadingWalletFace = false

        this.enable()

        this.openForIncomingEvents = true
        this.#bloomListIsDirty = true

        this.resize()

        const size = container.resolve<ISize>(Interface.ISize)
        size.onResize.add(this.resize)

        this.updateRoomSwipeAreas()

        window.setTimeout(() => {
          this.onWalletFaceLoadComplete.invoke()
          this.activeRoom.activateComplete()

          this.#rendererDebugInfo?.resetBuffer()

          resolve()
        }, 100)
      }

      return loadRoom()
    })
  }

  private gameLoop = () => {
    if (this.#running && !this.isDestroyed) {
      requestAnimationFrame(this.gameLoop)
    }

    let delta = this.#clock.getDelta()
    if (delta > 1) {
      delta = 1
    }

    this.#time += delta

    if (!this.#appPaused) {
      this.update(this.#time, delta)
    }
  }

  assetsUpdate(incomingAssets: Asset[], updateInfo: UpdateInfo) {
    //check if we have a coin it should not have
    for (const [existingCoinId, roomType] of this.#tokenRoomMap.entries()) {
      const tryGetAsset = incomingAssets.find(asset => asset.id === existingCoinId)
      if (!tryGetAsset) {
        /*if (tryGetAsset.ux.user.room !== roomType) {
          this.removeAsset(coinId)
          console.log('coin does not belong')
        }*/

        this.removeAsset(existingCoinId)
      }
    }

    this.doAddAssets(incomingAssets, updateInfo)
  }

  assetsAdded(assets: Asset[], updateInfo: UpdateInfo) {
    this.doAddAssets(assets, updateInfo)
  }

  doAddAssets(incomingTokens: Asset[], updateInfo: UpdateInfo, delay = 0): void {
    const updateUxDataList: Asset[] = []
    const allAssetPromises: Promise<void>[] = []

    //don't mind updates this way, they will be added when user click receive button
    if (!this.defaultRoom.config || updateInfo.refSource === RefSource.LinkReceive) {
      return
    }
    this.#shouldUseRevealAnimation = false

    const tokens = incomingTokens.filter((token: Asset) => supportedTokens.includes(token.t))
    tokens.forEach(token => {
      if (!token.ux.user.room) {
        token.ux.user.room = this.defaultRoom.config.id
      }

      let roomExists = false

      this.rooms.forEach(room => {
        if (room.config.id === token.ux.user.room) {
          roomExists = true
        }
      })

      if (!roomExists) {
        console.log(
          'Had to move coins over to ' + this.defaultRoom.config.id + ', ' + token.ux.user.room + ' does not exists'
        )
        token.ux.user.room = this.defaultRoom.config.id

        updateUxDataList.push(token)
      }

      allAssetPromises.push(this.createTokenInRoom(token, updateInfo))

      if (token.ux.user.fromWalletId) {
        const walletID = token.ux.user.fromWalletId
        let currentCount = 0
        if (this.newCoinsInWallets.has(walletID)) {
          currentCount = this.newCoinsInWallets.get(walletID)!
        }
        currentCount++
        this.newCoinsInWallets.set(walletID, currentCount)
      }

      if (token.ux.user.shouldBeRevealed) {
        this.#shouldUseRevealAnimation = true
      }
    })

    Promise.all(allAssetPromises).then(
      () => {
        //if created from topup
        if (updateInfo.refSource === RefSource.ParentCreate || updateInfo.refSource === RefSource.TokenRegister) {
          //this.defaultRoom.animateCoinFromActiveTube()
          this.triggerDelayedFlush(delay, false)
        } else {
          //if not from exchange machine and parent-create, it's from another user

          this.triggerDelayedFlush(
            delay,
            updateInfo.refSource !== RefSource.Combine &&
              updateInfo.refSource !== RefSource.Divide &&
              this.#shouldUseRevealAnimation
          )
        }

        this.updateTubeCoins()

        if (updateUxDataList.length > 0) {
          this.onUpdateUxData.invoke(updateUxDataList)
          updateUxDataList.length = 0
        }
      },
      error => {
        console.error('Could not load all coins:', error)
      }
    )
  }

  returnInvalidTransfer(asset: Asset) {
    this.#tokenRoomMap.delete(asset.id)
    this.doAddAssets([asset], { refSource: RefSource.Transfer, refId: '' }, 0.7)

    window.setTimeout(() => {
      this.activeRoom.animateCoinFromActiveTube()
    }, 700)
  }

  remotePoke(type: string, fromWalletId: string, data: any) {}

  triggerDelayedFlush(delay = 2, revealAnimation = false) {
    if (this.#activeWalletChangedTimeout) {
      window.clearTimeout(this.#activeWalletChangedTimeout)
      this.#activeWalletChangedTimeout = undefined
    }
    //wait 2 seconds until spawn new assets
    this.#activeWalletChangedTimeout = window.setTimeout(() => {
      this.newCoinsInWallets.set(this.activeContactWalletId, 0)
      if (this.activeRoom.flushWaitingList) {
        this.onNewAssetsAnimating.invoke()
        this.activeRoom.flushWaitingList(StaggerType.ALL, false, revealAnimation)

        this.#bloomListIsDirty = true
      }
    }, delay * 1000)
  }

  assetsRemoved(assetIds: string[]) {
    assetIds.forEach(asset => {
      this.removeAsset(asset)
    })

    this.#bloomListIsDirty = true
  }

  removeAsset(assetId: string) {
    this.#tokenRoomMap.delete(assetId)

    this.rooms.forEach(room => {
      room.assetRemoveByAssetId(assetId)
    })
  }

  createTokenInRoom(asset: Asset, updateInfo: UpdateInfo): Promise<void> {
    return new Promise((resolve, reject) => {
      let toRoomId = this.defaultRoom.config.id

      if (asset.ux.user.room) {
        toRoomId = asset.ux.user.room
      }

      let spawnLocation = SpawnLocation.InRoom

      switch (updateInfo.refSource) {
        case RefSource.Combine:
          spawnLocation = SpawnLocation.WalletCombine
          break

        case RefSource.Divide:
          spawnLocation = SpawnLocation.WalletDivide
          break

        case RefSource.Transfer:
          spawnLocation = SpawnLocation.Tube
          break

        case RefSource.ParentCreate:
          spawnLocation = SpawnLocation.Tube
          break
      }

      //if incoming coin has not been seen yet, animate from tube
      if (asset.ux.user.fromWalletId) {
        spawnLocation = SpawnLocation.Tube
      }

      if (updateInfo.refSource === RefSource.TokenRegister) {
        spawnLocation = SpawnLocation.Center
      }

      if (this.#tokenRoomMap.has(asset.id)) {
        resolve()
      } else {
        //create in room
        this.#tokenRoomMap.set(asset.id, toRoomId)

        const room = this.getRoomManager(toRoomId)
        if (room) {
          room
            .createTokenController({
              asset: asset,
              spawnLocation
            })
            .then(() => {
              resolve()
              this.#bloomListIsDirty = true
            })
        } else {
          reject()
          console.log('room' + toRoomId + ' does not exists, called to early?')
        }
      }

      this.#bloomListIsDirty = true
    })
  }

  //listens to the same event that VaultView uses to store new room on ux data
  protected onCoinChangeRoomHandler = (data: IMoveActionData) => {
    this.removeAsset(data.asset.id)

    const toRoomInstance = this.getRoomManager(data.toRoom)

    if (toRoomInstance) {
      this.#tokenRoomMap.set(data.asset.id, data.toRoom)
      const newRoomConnection = toRoomInstance.config.connectedRooms.find(c => c.id === data.fromRoom)
      if (newRoomConnection) {
        toRoomInstance.createTokenController({
          asset: data.asset,
          spawnLocation: this.getSpawnLocationFromSlot(newRoomConnection.slot)
        })
      }
    }

    this.#bloomListIsDirty = true
  }

  getSpawnLocationFromSlot(slot: RoomConnectionSlot) {
    switch (slot) {
      case RoomConnectionSlot.Top:
        return SpawnLocation.TopSlit
      case RoomConnectionSlot.Bottom:
        return SpawnLocation.BottomSlit
      case RoomConnectionSlot.Left:
        return SpawnLocation.LeftSlit
      case RoomConnectionSlot.Right:
        return SpawnLocation.RightSlit
    }

    return SpawnLocation.InRoom
  }

  // This can be overridden by subclass
  protected onTokenEnteredTubeHandler = (data: any) => {
    /*if (this.activeRoom.config.hasContacts) {
      const transferRoom = this.activeRoom as RoomTransferBase
      transferRoom.animateCoinIntoActiveTube()
    }*/
  }

  //override in subclass
  updateTubeCoins() {}

  cameraParallax(screenPosition: Vector2) {
    const size = container.resolve<ISize>(Interface.ISize).getSize()
    let toX = (screenPosition.x / size.width) * 2 - 1
    let toY = (screenPosition.y / size.height) * 2 - 1

    toX *= 0.25
    toY *= -0.25

    this.#cameraParallaxOffsetTarget.x = toX
    this.#cameraParallaxOffsetTarget.y = toY
  }

  /* 
  Used to detect taps on objects in 3d scene
  */
  raycastTriggers(screenX: number, screenY: number) {
    const size = container.resolve<ISize>(Interface.ISize).getSize()
    const clipX = (screenX / size.width) * 2 - 1
    const clipY = -(screenY / size.height) * 2 + 1
    const mouse = new Vector2(clipX, clipY)

    raycaster.setFromCamera(mouse, this.activeRoom.camera)

    const intersects = raycaster.intersectObjects(this.activeRoom.sceneTriggerColliders, true)
    if (intersects.length > 0) {
      const triggerData = intersects[0].object.userData
      if (triggerData.type === ColliderType.NAVIGATION) {
        //block navigation from clicks on slits that is not currently used
        const connection = this.activeRoom.config.connectedRooms.find(c => c.slot === triggerData.slot)
        if (connection) {
          this.onRoomChangeRequest.invoke(connection.id)
        }
      } else if (triggerData.type === ColliderType.ACTION) {
        triggerData.uv = intersects[0].uv

        this.activeRoom.handleRaycastHit(triggerData)
      }
    }

    //this.activeRoom.closeOpenCoins()
  }

  animateToRoom(toRoomType: string) {
    const toRoom = this.getRoomManager(toRoomType)

    if (toRoom === undefined || this.#isInTransition) {
      return
    }

    const touch = container.resolve<ITouch>(Interface.ITouch)
    touch.unregisterSwipeArea('nav-down')
    touch.unregisterSwipeArea('nav-up')

    this.#isInTransition = true
    toRoom.visible = true

    let animationOffset = 0
    const toConnection = this.activeRoom.config.connectedRooms.find(c => c.id === toRoomType)
    if (toConnection) {
      if (toConnection.slot === RoomConnectionSlot.Bottom) {
        animationOffset = 4
      } else if (toConnection.slot === RoomConnectionSlot.Top) {
        animationOffset = -4
      }
    }

    this.#openForIncomingEvents = false

    this.activeRoom.inactivate()

    const tweenObj = { value: 0 }
    gsap.to(tweenObj, {
      duration: 0.2,
      delay: CAMERA_ANIMATION_DURATION_OUT - 0.2,
      value: 1,
      onUpdate: () => {
        this.postEffectManager.fadeAmount = tweenObj.value
      }
    })

    const assets = container.resolve<IAssets>(Interface.IAssets)

    //fade out
    gsap.to(this.activeRoom.cameraPivot.position, {
      duration: CAMERA_ANIMATION_DURATION_OUT,
      y: this.activeRoom.config.cameraPosition.y - animationOffset,
      ease: Expo.easeIn,
      onComplete: () => {
        if (toRoom.isLoaded) {
          this.roomAnimateIn(toRoom, animationOffset)
        } else {
          /*const loadingManifest = toRoom.getAdjustedManifest()
          loadingManifest.forEach(item => {
            item.url = walletFaces.getUrl(item.url, this.walletFaceId)
          })

          assets
            .loadRoomManifest(toRoom.config.id, loadingManifest)
            .then(() => {
              return toRoom.init()
            })
            .then(() => {
              //toRoom.addItems(getItemUsedForRoom(getRoom(getVault(), toRoom.type)))
              this.roomAnimateIn(toRoom, animationOffset)
            })
            */
        }
      }
    })
  }

  roomAnimateIn(toRoom: RoomBase, animationOffset: number) {
    this.activeRoom = toRoom

    this.postEffectManager.setSceneAndCamera(this.activeRoom.scene, this.activeRoom.camera as Camera)
    this.#bloomListIsDirty = true
    this.activeRoom.activate()
    this.activeRoom.resize()

    const tweenObj = { value: 1 }
    gsap.to(tweenObj, {
      duration: 0.2,
      value: 0,
      onUpdate: () => {
        this.postEffectManager.fadeAmount = tweenObj.value
      }
    })
    gsap.fromTo(
      this.activeRoom.cameraPivot.position,
      { y: this.activeRoom.config.cameraPosition.y + animationOffset },
      {
        duration: CAMERA_ANIMATION_DURATION_IN,
        y: this.activeRoom.config.cameraPosition.y,
        ease: Expo.easeOut,
        onComplete: () => {
          this.#isInTransition = false
          this.onRoomTransitionComplete.invoke(toRoom.config.id)
          this.activeRoom.activateComplete()
          this.openForIncomingEvents = true
          this.enableInteractions()
          this.updateRoomSwipeAreas()
        }
      }
    )
  }

  onTap = (tap: MouseEvent) => {
    this.raycastTriggers(tap.clientX, tap.clientY)
  }

  getRoomManager(type: string): RoomBase {
    return this.rooms.get(type)!
  }

  update(time: number, delta: number) {
    if (this.isDestroyed || !this.#running) {
      return
    }

    this.activeRoom.update(time, delta)

    //this.#cameraParallaxOffsetInterpolated.lerp(this.#cameraParallaxOffsetTarget, 0.05)
    //this.activeRoom.camera.position.lerp(this.#cameraParallaxOffsetInterpolated, 0.02)

    if (globalRenderer.usePostEffects) {
      if (this.#bloomListIsDirty) {
        this.postEffectManager.updateBloomList()
        this.#bloomListIsDirty = false
      }

      this.postEffectManager.render(time, delta)
    } else {
      this.#renderer.render(this.activeRoom.scene, this.activeRoom.camera)
    }

    if (this.#rendererDebugInfo) {
      this.#rendererDebugInfo.update(delta)
      this.onRenderInfo.invoke({ fps: this.#rendererDebugInfo.fps, drawCalls: this.#rendererDebugInfo.drawCalls })
    }

    this.#renderer.info.reset()

    this.activeRoom.renderOverlay()

    this.onUpdate.invoke()
    this.hasRenderedOnce = true
  }

  resize = () => {
    if (this.isLoadingWalletFace) {
      return
    }

    const size = container.resolve<ISize>(Interface.ISize).getSize()

    this.rooms.forEach(room => {
      //if (room.isLoaded) {
      const cameraAsPerspectiveType = room.camera as PerspectiveCamera
      cameraAsPerspectiveType.aspect = size.width / size.height
      cameraAsPerspectiveType.updateProjectionMatrix()
      room.resize()
      //}
    })

    const debug = container.resolve<IDebug>(Interface.IDebug)
    if (debug.getFlag(DebugFlag.RenderDownscale)) {
      this.#renderer.setSize(size.width / size.sizeRatio, size.height / size.sizeRatio)

      if (size.sizeRatio > 1) {
        this.#renderer.domElement.style.transform = 'scale3d(' + size.sizeRatio + ', ' + size.sizeRatio + ', 1)'
        this.#renderer.domElement.style.transformOrigin = '0 0 0'
        this.#renderer.domElement.style.webkitTransform = 'scale3d(' + size.sizeRatio + ', ' + size.sizeRatio + ', 1)'
        this.#renderer.domElement.style.webkitTransformOrigin = '0 0 0'
      }
    } else {
      this.#renderer.setSize(size.width, size.height)
    }

    this.postEffectManager.resize()
  }

  updateBloomList() {
    this.#bloomListIsDirty = true
  }

  removeAllTubes() {
    if (!this.defaultRoom.config) {
      return
    }
    const room = this.getRoomManager(this.defaultRoom.config.id) as RoomBase
    if (room) {
      room.removeAllTubes()
      this.#bloomListIsDirty = true
    }
  }

  hideAllTubes(): Promise<void> {
    if (
      this.defaultRoom.config &&
      this.getRoomManager(this.defaultRoom.config.id) &&
      this.getRoomManager(this.defaultRoom.config.id).hideAllTubes
    ) {
      return this.getRoomManager(this.defaultRoom.config.id).hideAllTubes()
    }

    return Promise.resolve()
  }

  unloadRooms() {
    const assets = container.resolve<IAssets>(Interface.IAssets)
    this.rooms.forEach(room => {
      room.destroy()
      room.onCoinZoomedIn.removeAll()

      assets.unloadNamespace(room.config.id)

      const sound = container.resolve<ISound>(Interface.ISound)
      sound.unregisterSoundMap('vault' + this.walletFaceId + '_' + this.activeRoom.config.id)

      room.onTubeClosed.removeAll()
      room.onCoinZoomedIn.removeAll()
      room.onCoinRemove.removeAll()
      room.onCoinsCombine.removeAll()
      room.onCoinDivide.removeAll()
      room.onCoinChangeRoom.removeAll()
      room.onTokenEnteredTube.removeAll()
      room.onNewAssetsArrived.removeAll()
      room.onUpdateBloomList.removeAll()
      room.onRevealStarted.removeAll()
    })
    this.rooms.clear()

    //todo, remove reference to vault in room
    this.activeRoom = {} as RoomBase
  }

  destroy() {
    this.disable()

    this.isDestroyed = true
    const size = container.resolve<ISize>(Interface.ISize)
    size.onResize.remove(this.resize)

    this.#running = false

    const sound = container.resolve<ISound>(Interface.ISound)
    sound.unregisterSoundMap('vault' + this.walletFaceId)

    const touch = container.resolve<ITouch>(Interface.ITouch)
    touch.unregisterSwipeArea('nav-down')
    touch.unregisterSwipeArea('nav-up')

    this.onOpenForEventChanged.removeAll()

    this.unloadRooms()

    if (this.postEffectManager && this.postEffectManager.dispose) {
      this.postEffectManager.dispose()
    }
  }
}
